import { getProductTypeFromMetadata } from './index';

export const HANDLE_BASE_CHOICE_ID = 275270;
export const HANDLE_FIRST_CHOICE_ID = 275268;
export const HANDLE_FIRST_BUNDLE_CHOICE_ID = 278438;
export const HANDLE_AREA = 'handle';

export const isHandleStarterChoiceId = (choiceId) => {
  return choiceId === HANDLE_FIRST_CHOICE_ID || choiceId === HANDLE_FIRST_BUNDLE_CHOICE_ID;
};

const splitHandleColumns = (products = []) => ({
  leftProducts: products.filter((product) => product.Origin[1] < 2),
  rightProducts: products.filter((product) => product.Origin[1] >= 2)
});

// This is the handle part: build fast lookups for handle-only
// placement and drag rules.
export const buildHandleIndexing = (handleProducts = [], metadata = {}) => {
  return handleProducts.reduce((acc, product, index) => {
    const type = getProductTypeFromMetadata(metadata?.[product.choiceId]?.metadata);
    acc.indexByStackItemId[product.stackItemId] = index;
    acc.typeByStackItemId[product.stackItemId] = type;
    if (type === 'wide') {
      acc.wideIndexes.push(index);
    }
    return acc;
  }, {
    indexByStackItemId: {},
    typeByStackItemId: {},
    wideIndexes: []
  });
};

// This is the handle part: handle stacks alternate between single wide groups and multi narrow groups.
export const buildHandleStackGroups = (handleProducts = [], handleIndexing) => {
  const groups = [];
  let currentGroup = null;

  handleProducts.forEach((product) => {
    const productType = handleIndexing.typeByStackItemId[product.stackItemId];
    const stackType = productType === 'wide' ? 'single' : 'multi';
    if (!currentGroup || currentGroup.type !== stackType) {
      if (currentGroup) groups.push(currentGroup);
      currentGroup = { type: stackType, products: [] };
    }
    currentGroup.products.push(product);
  });

  if (currentGroup) groups.push(currentGroup);
  return groups;
};

// This is the handle part: stack height drives the floating handle dropzone position.
export const calculateHandleStackHeight = (handleStackGroups, getProductHeight) => {
  if (!handleStackGroups?.length) return 0;
  if (typeof document === 'undefined') return 0;

  const cs = getComputedStyle(document.documentElement);
  const baseFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const parseRemVar = (val) => parseFloat(val.trim()) * baseFontSize || 0;
  const leftGapPx = parseRemVar(cs.getPropertyValue('--stacks-left-gap'));
  const rightGapPx = parseRemVar(cs.getPropertyValue('--stacks-right-gap'));
  const marginBottomPx = parseRemVar(cs.getPropertyValue('--stacks-left-margin-bottom'));

  let totalHeight = 0;
  handleStackGroups.forEach((group) => {
    if (group.type === 'single') {
      const groupHeight = group.products.reduce((sum, product) => sum + getProductHeight(product.choiceId), 0);
      totalHeight += groupHeight;
      return;
    }

    const { leftProducts, rightProducts } = splitHandleColumns(group.products);
    const leftHeight = leftProducts.reduce((sum, product) => sum + getProductHeight(product.choiceId), 0)
      + Math.max(leftProducts.length - 1, 0) * leftGapPx;
    const rightHeight = rightProducts.reduce((sum, product) => sum + getProductHeight(product.choiceId), 0)
      + Math.max(rightProducts.length - 1, 0) * rightGapPx;
    totalHeight += Math.max(leftHeight, rightHeight) + marginBottomPx;
  });

  return totalHeight;
};

// This is the handle part: guard the starter item and prevent pulling support narrows from under a wide.
export const getHandleDraggabilityState = ({
  product,
  baseDraggable,
  handleIndexing,
  firstHandleProduct,
  firstHandleProductImmediateAbove,
}) => {
  const productType = handleIndexing.typeByStackItemId?.[product.stackItemId];
  const productIndex = handleIndexing.indexByStackItemId?.[product.stackItemId];

  if (
    productType === 'narrow'
    && Number.isInteger(productIndex)
    && handleIndexing.wideIndexes.some((wideIndex) => wideIndex > productIndex)
  ) {
    return false;
  }

  if (!firstHandleProduct || product?.stackItemId !== firstHandleProduct?.stackItemId) {
    return baseDraggable;
  }

  const isRequiredFirstChoice = isHandleStarterChoiceId(firstHandleProduct?.choiceId);
  const hasNoImmediateAbove = !firstHandleProductImmediateAbove;
  const hasMatchingImmediateAbove = firstHandleProductImmediateAbove?.choiceId === firstHandleProduct?.choiceId;

  return baseDraggable
    && isRequiredFirstChoice
    && (hasNoImmediateAbove || hasMatchingImmediateAbove);
};

// This is the handle part: wide placement depends on narrow height symmetry after the first item.
export const getHandleEligibleDropZones = ({
  shouldShowHandleDropzone,
  shouldShowHandleStack,
  dropZones,
  handleProducts,
  handleIndexing,
  getProductHeight,
}) => {
  if (!shouldShowHandleDropzone || !dropZones?.length) return [];
  if (!shouldShowHandleStack) {
    const firstWideZone = dropZones.find((zone) => zone?.type === 'wide');
    return firstWideZone ? [{ ...firstWideZone, isBlocked: false }] : [];
  }

  const { leftNarrowHeight, rightNarrowHeight } = handleProducts.reduce((acc, product) => {
    const type = handleIndexing.typeByStackItemId[product.stackItemId];
    if (type !== 'narrow') return acc;

    const height = getProductHeight(product.choiceId);
    if (product.Origin[1] < 2) {
      return { ...acc, leftNarrowHeight: acc.leftNarrowHeight + height };
    }
    return { ...acc, rightNarrowHeight: acc.rightNarrowHeight + height };
  }, { leftNarrowHeight: 0, rightNarrowHeight: 0 });

  const heightsMismatch = leftNarrowHeight !== rightNarrowHeight;
  return dropZones
    .filter((zone) => zone?.type !== 'full')
    .map((zone) => ({
      ...zone,
      isBlocked: heightsMismatch && zone.type === 'wide'
    }));
};
